import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

import { getEntityOwnershipText } from 'common/utility/helpers';
import { makeFullAddressDisplay } from '@utilityprofit/address-helpers';
import {
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_TYPE_ID_INTERNET,
} from 'common/utility/constants';

import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrDividerLine from 'client/components/SrDividerLine';
import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_RED,
} from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

const formFields = [
  {
    id: 'cancelReasonOther',
    type: 'long_text',
    properties: {
      placeholder: 'Reason for archiving',
    },
    validations: ['required'],
  },
];

/**
 * `orderPlacedAt` and a `completed` status are independent signals — an item can complete
 * without this app ever seeing the placement timestamp, so neither alone is sufficient.
 */
export const hasLiveInternetOrder = (activation) =>
  Boolean(
    activation.items?.some(
      (item) =>
        item.utilityTypeId === UTILITY_TYPE_ID_INTERNET &&
        (item.orderPlacedAt || item.status === UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED),
    ),
  );

const SrUtilityActivationCancelModal = ({ onClose, onSubmit, activation }) => {
  const [liveOrderConfirmed, setLiveOrderConfirmed] = useState(!hasLiveInternetOrder(activation));
  const tenantName = activation.user?.firstName;
  const address = makeFullAddressDisplay(activation.address);
  const title = tenantName
    ? `Archiving ${getEntityOwnershipText({ name: tenantName, word: 'Utilities' })}`
    : 'Archiving Utilities';

  return (
    <SrDarkModal onClose={onClose}>
      <SrModalContent style={{ width: 553, marginTop: 4 }}>
        <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
          <SrText
            font="greycliff"
            fontSize="26px"
            weight="800"
            lineHeight="36px"
            letterSpacing="0.5"
          >
            {title}
          </SrText>
        </SrBox>
        <SrDividerLine text={address} alignItems="center" />
        {liveOrderConfirmed ? (
          <SrFlex marginTop="8px">
            <Formik
              initialValues={createInitialValues({
                formFields: formFields,
                defaultValues: {
                  cancelReason: '',
                },
              })}
              validate={(formValues) => {
                return createFormValidationFn(formFields)(formValues);
              }}
              onSubmit={onSubmit}
              render={(formikBag) => {
                return (
                  <Form style={{ width: '100%' }}>
                    <SrBox margin="20px 0">
                      {formFields.map((formField) => {
                        return (
                          <SrFormikField
                            key={formField.id}
                            formField={formField}
                            formikBag={formikBag}
                            className={SR_FIELD_THEME_ADOBE}
                          />
                        );
                      })}
                    </SrBox>
                    <SrBox className="submitButtonContainer">
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_RED}
                        onClick={formikBag.submitForm}
                        disabled={!formikBag.isValid}
                      >
                        Archive
                      </SrButton>
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREY}
                        margin="12px 0px"
                        onClick={onClose}
                      >
                        Cancel
                      </SrButton>
                    </SrBox>
                  </Form>
                );
              }}
            />
          </SrFlex>
        ) : (
          <SrFlex marginTop="8px" flexDirection="column" width="100%">
            <SrBox margin="20px 0">
              <SrText font="greycliff" fontSize="16px" weight="500" lineHeight="24px">
                This move has an active internet order. Archiving it here will not cancel the order
                — archive anyway?
              </SrText>
            </SrBox>
            <SrBox className="submitButtonContainer">
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_RED}
                onClick={() => setLiveOrderConfirmed(true)}
              >
                Archive anyway
              </SrButton>
              <SrButton theme={SR_BUTTON_THEME_ADOBE_GREY} margin="12px 0px" onClick={onClose}>
                Cancel
              </SrButton>
            </SrBox>
          </SrFlex>
        )}
      </SrModalContent>
    </SrDarkModal>
  );
};

SrUtilityActivationCancelModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  activation: PropTypes.object.isRequired,
};

export default SrUtilityActivationCancelModal;
